import { useState } from "preact/hooks";
import { api, ApiError } from "../api";

type Mode = "login" | "forgot" | "reset";

export function Login({ onAuthed }: { onAuthed: () => void }) {
  const [mode, setMode] = useState<Mode>("login");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<void>) {
    setErr(""); setBusy(true);
    try { await fn(); } catch (e) { setErr(e instanceof ApiError ? e.message : "Something went wrong"); }
    finally { setBusy(false); }
  }

  const doLogin = (e: Event) => { e.preventDefault(); run(async () => { await api.login(password); onAuthed(); }); };
  const doForgot = (e: Event) => {
    e.preventDefault();
    run(async () => { await api.forgot(); setInfo("If email is set up, a 6-digit code is on its way."); setMode("reset"); });
  };
  const doReset = (e: Event) => {
    e.preventDefault();
    run(async () => { await api.reset(code.trim(), newPassword); onAuthed(); });
  };

  return (
    <div class="center-screen fade-in">
      <div class="brand-row">
        <img src="/icons/icon.svg" alt="" />
        <span class="name">Hickman Health</span>
      </div>

      {mode === "login" && (
        <form onSubmit={doLogin}>
          <h1 style={{ fontSize: 26, marginBottom: 6 }}>Welcome back.</h1>
          <p class="muted" style={{ marginBottom: 26 }}>Enter your password to continue.</p>
          <div class="field">
            <label>Password</label>
            <input type="password" autocomplete="current-password" value={password}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)} placeholder="••••••••••" />
          </div>
          <div class="err">{err}</div>
          <button class="btn" type="submit" disabled={busy || !password}>{busy ? "Signing in…" : "Sign in"}</button>
          <button type="button" class="btn ghost" onClick={() => { setErr(""); setInfo(""); setMode("forgot"); }}>
            Forgot password?
          </button>
        </form>
      )}

      {mode === "forgot" && (
        <form onSubmit={doForgot}>
          <h1 style={{ fontSize: 26, marginBottom: 6 }}>Reset password</h1>
          <p class="muted" style={{ marginBottom: 26 }}>We'll email a 6-digit code to your registered address.</p>
          <div class="err">{err}</div>
          <button class="btn" type="submit" disabled={busy}>{busy ? "Sending…" : "Email me a code"}</button>
          <button type="button" class="btn ghost" onClick={() => { setErr(""); setMode("login"); }}>Back to sign in</button>
        </form>
      )}

      {mode === "reset" && (
        <form onSubmit={doReset}>
          <h1 style={{ fontSize: 26, marginBottom: 6 }}>Enter your code</h1>
          {info && <p class="muted" style={{ marginBottom: 20 }}>{info}</p>}
          <div class="field">
            <label>6-digit code</label>
            <input inputMode="numeric" autocomplete="one-time-code" value={code}
              onInput={(e) => setCode((e.target as HTMLInputElement).value)} placeholder="123456" />
          </div>
          <div class="field">
            <label>New password</label>
            <input type="password" autocomplete="new-password" value={newPassword}
              onInput={(e) => setNewPassword((e.target as HTMLInputElement).value)} placeholder="at least 8 characters" />
          </div>
          <div class="err">{err}</div>
          <button class="btn" type="submit" disabled={busy || code.length < 6 || newPassword.length < 8}>
            {busy ? "Resetting…" : "Set new password"}
          </button>
          <button type="button" class="btn ghost" onClick={() => { setErr(""); setMode("forgot"); }}>Resend code</button>
        </form>
      )}
    </div>
  );
}
